import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type Row,
  type RowSelectionState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, FilterX } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../lib/cn';
import { Button } from './button';
import { Checkbox } from './field';
import { Skeleton } from './surface';

/** Optional per-column layout hints, set through `meta` on a column definition. */
export interface DataColumnMeta {
  /** Tailwind classes for the <td>. */
  className?: string;
  headerClassName?: string;
  align?: 'left' | 'right' | 'center';
  /** Hide below this breakpoint to keep tablet layouts readable. */
  hideBelow?: 'md' | 'lg' | 'xl';
  /** The cell holds its own control (a switch, a button): a click on it must not also fire `onRowClick`. */
  stopRowClick?: boolean;
}

// Stable fallbacks: TanStack Table memoizes its row models on the identity of these values, so a fresh `[]`/`{}` on every
// render would recompute the row model each time, which re-queues an internal state update and re-renders in a loop.
const NO_SORTING: SortingState = [];
const NO_VISIBILITY: VisibilityState = {};
const NO_SELECTION: RowSelectionState = {};

/**
 * True while the element's content is wider than the element. A horizontally scrolling region must be reachable by keyboard
 * (WCAG 2.1.1), but only then: an always-focusable wrapper would add a pointless tab stop to every table.
 */
function useOverflowsX<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [overflows, setOverflows] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const check = () => setOverflows(el.scrollWidth > el.clientWidth + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, []);
  return [ref, overflows] as const;
}

const hideClass = { md: 'max-md:hidden', lg: 'max-lg:hidden', xl: 'max-xl:hidden' } as const;
const alignClass = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

export interface DataTableProps<T> {
  columns: ColumnDef<T, any>[];
  data: T[];
  /** Accessible name for the table (announced by screen readers). */
  caption: string;
  getRowId?: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  skeletonRows?: number;
  /** Rendered instead of the body when there is no data (and not loading). */
  empty?: ReactNode;
  /** Optional line rendered inside the table after the last data row, for inline creation. */
  footer?: ReactNode;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  /** Sorting happens on the server; do not sort locally. */
  manualSorting?: boolean;
  columnVisibility?: VisibilityState;
  selectedId?: string | null;
  className?: string;
  /** Adds a leading checkbox column: header selects/clears every selectable row, each row gets its own. */
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (next: RowSelectionState) => void;
  /** A row with no checkbox at all (e.g. a system record that can never be bulk-acted on). Selectable by default. */
  isRowSelectable?: (row: T) => boolean;
}

export function DataTable<T>({
  columns,
  data,
  caption,
  getRowId,
  onRowClick,
  loading,
  skeletonRows = 8,
  empty,
  footer,
  sorting,
  onSortingChange,
  manualSorting,
  columnVisibility,
  selectedId,
  className,
  enableRowSelection,
  rowSelection,
  onRowSelectionChange,
  isRowSelectable,
}: DataTableProps<T>) {
  const selectionColumn = useMemo<ColumnDef<T, any> | null>(() => {
    if (!enableRowSelection) return null;
    return {
      id: '__select',
      size: 40,
      // `table`/`row` are read at call time, from TanStack's own instance for the current render — never close over this
      // component's own props here. A closure captured at column-definition time would need `rowSelection`/
      // `onRowSelectionChange` in this memo's deps to stay fresh, which would recreate `cell` on every selection change;
      // `flexRender` treats a changed cell-function identity as a new component type and remounts the checkbox (a fresh
      // DOM node, a fresh `useId()`) on every click instead of just re-rendering it in place.
      header: ({ table }) => {
        const checked = table.getIsAllRowsSelected() ? true : table.getIsSomeRowsSelected() ? 'indeterminate' : false;
        const hasSelectableRows = table.getRowModel().rows.some((row) => row.getCanSelect());
        return (
          <Checkbox
            label={<span className="sr-only">Select all rows</span>}
            checked={checked}
            disabled={!hasSelectableRows}
            onCheckedChange={(next) => table.toggleAllRowsSelected(next === true)}
          />
        );
      },
      cell: ({ row }: { row: Row<T> }) =>
        row.getCanSelect() ? (
          <Checkbox
            label={<span className="sr-only">Select row</span>}
            checked={row.getIsSelected()}
            onCheckedChange={(next) => row.toggleSelected(next === true)}
          />
        ) : null,
      meta: { className: 'w-10', headerClassName: 'w-10' } satisfies DataColumnMeta,
    };
  }, [enableRowSelection]);
  const allColumns = selectionColumn ? [selectionColumn, ...columns] : columns;

  const table = useReactTable({
    data,
    columns: allColumns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    manualSorting,
    state: { sorting: sorting ?? NO_SORTING, columnVisibility: columnVisibility ?? NO_VISIBILITY, rowSelection: rowSelection ?? NO_SELECTION },
    onSortingChange: onSortingChange
      ? (updater) => onSortingChange(typeof updater === 'function' ? updater(sorting ?? NO_SORTING) : updater)
      : undefined,
    enableRowSelection: isRowSelectable ? (row) => isRowSelectable(row.original) : true,
    onRowSelectionChange: onRowSelectionChange
      ? (updater) => onRowSelectionChange(typeof updater === 'function' ? updater(rowSelection ?? NO_SELECTION) : updater)
      : undefined,
    enableSortingRemoval: true,
    // Paging is done by the server and the Pagination component, never by the table itself.
    autoResetPageIndex: false,
  });

  const [scrollRef, scrolls] = useOverflowsX<HTMLDivElement>();
  const rows = table.getRowModel().rows;
  const visibleColumns = table.getVisibleLeafColumns();
  const showEmpty = !loading && rows.length === 0 && empty;

  function onKeyDown(e: KeyboardEvent<HTMLTableRowElement>, row: T) {
    if (!onRowClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick(row);
    }
  }

  return (
    <div className={cn('overflow-hidden rounded-lg border bg-surface shadow-xs', className)}>
      <div
        ref={scrollRef}
        className="overflow-x-auto"
        {...(scrolls ? { tabIndex: 0, role: 'region', 'aria-label': `${caption}, scrolls horizontally` } : {})}
      >
        <table aria-busy={loading || undefined} className="w-full border-collapse text-left text-base">
          <caption className="sr-only">{caption}</caption>
          <thead className="sticky top-0 z-10 bg-surface-2">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b">
                {hg.headers.map((header) => {
                  const meta = header.column.columnDef.meta as DataColumnMeta | undefined;
                  const sortable = header.column.getCanSort() && !!onSortingChange;
                  const dir = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : sortable ? 'none' : undefined}
                      className={cn(
                        'h-10 whitespace-nowrap px-4 text-xs font-medium text-muted',
                        alignClass[meta?.align ?? 'left'],
                        meta?.hideBelow && hideClass[meta.hideBelow],
                        meta?.headerClassName,
                      )}
                    >
                      {header.isPlaceholder ? null : sortable ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="-mx-1.5 inline-flex items-center gap-1 rounded-md px-1.5 py-1 hover:bg-sunken hover:text-fg"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {dir === 'asc' ? (
                            <ArrowUp className="size-3.5 text-brand-text" aria-hidden />
                          ) : dir === 'desc' ? (
                            <ArrowDown className="size-3.5 text-brand-text" aria-hidden />
                          ) : (
                            <ChevronsUpDown className="size-3.5 text-disabled" aria-hidden />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: skeletonRows }, (_, i) => (
                  <tr key={i} className="h-(--row-h) border-b last:border-b-0">
                    {visibleColumns.map((col, ci) => {
                      const meta = col.columnDef.meta as DataColumnMeta | undefined;
                      return (
                        <td key={col.id} className={cn('px-4', meta?.hideBelow && hideClass[meta.hideBelow])}>
                          <Skeleton className={cn('h-4', ci === 0 ? 'w-40' : 'w-20')} />
                        </td>
                      );
                    })}
                  </tr>
                ))
              : rows.map((row) => {
                  const selected = (selectedId != null && row.id === selectedId) || row.getIsSelected();
                  return (
                    <tr
                      key={row.id}
                      tabIndex={onRowClick ? 0 : undefined}
                      aria-selected={selected || undefined}
                      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                      onKeyDown={onRowClick ? (e) => onKeyDown(e, row.original) : undefined}
                      className={cn(
                        'h-(--row-h) border-b transition-colors last:border-b-0',
                        onRowClick && 'cursor-pointer hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-offset-[-2px]',
                        selected && 'bg-brand-subtle hover:bg-brand-subtle-hover',
                      )}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const meta = cell.column.columnDef.meta as DataColumnMeta | undefined;
                        const isSelectionCell = cell.column.id === '__select';
                        return (
                          <td
                            key={cell.id}
                            onClick={isSelectionCell || meta?.stopRowClick ? (e) => e.stopPropagation() : undefined}
                            className={cn(
                              'px-4 py-2 align-middle',
                              alignClass[meta?.align ?? 'left'],
                              meta?.hideBelow && hideClass[meta.hideBelow],
                              meta?.className,
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            {!loading && footer ? (
              <tr className="border-t bg-surface">
                <td colSpan={visibleColumns.length} className="p-0">
                  {footer}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {showEmpty ? <div className="border-t">{empty}</div> : null}
    </div>
  );
}

/* --------------------------------------------------------------- FilterBar */

export function FilterBar({
  children,
  activeCount = 0,
  onClear,
  className,
}: {
  children: ReactNode;
  activeCount?: number;
  onClear?: () => void;
  className?: string;
}) {
  return (
    <div role="group" aria-label="Filters" className={cn('flex flex-wrap items-center gap-2', className)}>
      {children}
      {activeCount > 0 && onClear ? (
        <Button variant="ghost" size="sm" onClick={onClear} leadingIcon={<FilterX />}>
          Clear filters
          <span className="rounded-full bg-brand-subtle px-1.5 text-xs font-semibold tabular-nums text-brand-text">{activeCount}</span>
        </Button>
      ) : null}
    </div>
  );
}
